import { throttle } from 'lodash';
import React, { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { Redirect, useLocation } from 'react-router';

import { CacheControl } from '@swf/components/ResponseHeaders/ResponseHeaders';
import StarsModal from '@swf/components/StarsModal/StarsModal';
import StarsSection from '@swf/components/StarsSection/StarsSection';
import type { PageState } from '@swf/core/models/page';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { StaticContext } from '@swf/core/types/StaticContext';
import { absoluteURL, canUseDOM } from '@swf/core/utils';

import AlternateLinks from './components/AlternateLinks/AlternateLinks';
import CanonicalLink from './components/CanonicalLink/CanonicalLink';
import MetaDescription from './components/MetaDescription/MetaDescription';
import MetaNoIndex from './components/MetaNoIndex/MetaNoIndex';
import MetaTitle from './components/MetaTitle/MetaTitle';
import NotFound from './components/NotFound/NotFound';

interface Props {
  page?: PageState;
  staticContext?: RouteComponentProps<Record<string, string>, StaticContext>['staticContext'];
}

const StarsPage: React.FC<Props> = ({ page, staticContext = {} }) => {
  const storePage = useSelector(select.page.get);
  const { pathname } = useLocation();
  const { page: pageDispatch } = useDispatch<Dispatch>();
  const prevPathname = useRef(pathname);
  const { layout, meta } = page || storePage;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(
    throttle(() => {
      if (prevPathname.current !== pathname) {
        prevPathname.current = pathname;
        pageDispatch.fetchPage({ href: window.location.href });
      }
    }, 200),
    [pageDispatch, pathname]
  );

  useEffect(() => {
    if (!layout) pageDispatch.fetchPage({ href: window.location.href });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  staticContext.prefetch = ({ dispatch }, options) => dispatch.page.fetchPage(options);
  staticContext.statusCode = layout ? 200 : 404;

  if (meta?.redirect && meta.redirect.statusCode && meta.redirect.statusCode >= 300) {
    staticContext.statusCode = meta.redirect.statusCode;
    const { location } = meta.redirect;

    if (absoluteURL(location)) {
      canUseDOM ? (window.location.href = location) : (staticContext.url = location);
      return null;
    }

    return <Redirect to={location} />;
  }

  return (
    <>
      <MetaTitle />
      <MetaDescription />
      <MetaNoIndex />
      <AlternateLinks />
      <CanonicalLink />

      {layout ? (
        <StarsSection layout={layout} />
      ) : (
        <>
          <CacheControl>no-store</CacheControl>
          <NotFound />
        </>
      )}
      <StarsModal />
    </>
  );
};

export default StarsPage;
